import React, { useState, useEffect } from 'react';
import { Link2, Info, CheckCircle, ExternalLink, RefreshCw } from 'lucide-react';
import api from '../services/api';
import Layout from '../components/Layout';
import styles from './IntegracoesPage.module.css';

interface BlingStatus {
    authenticated: boolean;
    token_type?: string;
    scope?: string;
    expires_in?: number;
    is_expired?: boolean;
    expires_at?: string;
    error?: string;
}

interface BlingConfig {
    clientId: string | null;
    redirectUri: string | null;
    scopes: string | null;
    baseUrl: string | null;
    authorizationUrl: string | null;
    tokenUrl: string | null;
    hasClientSecret: boolean;
}

const IntegracoesPage: React.FC = () => {
    const [blingStatus, setBlingStatus] = useState<BlingStatus | null>(null);
    const [blingConfig, setBlingConfig] = useState<BlingConfig | null>(null);
    const [configForm, setConfigForm] = useState({
        clientId: '',
        clientSecret: '',
        redirectUri: '',
        scopes: '',
    });
    const [configSaving, setConfigSaving] = useState(false);
    const [configMessage, setConfigMessage] = useState<string | null>(null);
    const [configError, setConfigError] = useState<string | null>(null);
    const [restartLoading, setRestartLoading] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchBlingStatus = async () => {
        setLoading(true);
        try {
            const response = await api.get('/bling/status');
            setBlingStatus(response.data);
        } catch (error) {
            console.error('Erro ao buscar status do Bling:', error);
            setBlingStatus({ authenticated: false, error: 'Erro ao conectar com o serviço' });
        } finally {
            setLoading(false);
        }
    };

    const fetchBlingConfig = async () => {
        try {
            const response = await api.get('/bling/config');
            const config: BlingConfig = response.data.config;
            setBlingConfig(config);
            setConfigForm((prev) => ({
                ...prev,
                clientId: config.clientId || '',
                redirectUri: config.redirectUri || '',
                scopes: config.scopes || '',
            }));
        } catch (error) {
            console.error('Erro ao buscar configuracao do Bling:', error);
            setConfigError('Erro ao buscar configuracao do Bling');
        }
    };

    useEffect(() => {
        fetchBlingStatus();
        fetchBlingConfig();
    }, []);

    const handleConnectBling = () => {
        // Redireciona para o endpoint de autenticação do backend
        window.location.href = `${api.defaults.baseURL || ''}/bling/auth`;
    };

    const handleRestartBling = async () => {
        setRestartLoading(true);
        try {
            // Limpa tokens para forcar novo consentimento (novo escopo).
            await api.post('/bling/logout');
            await fetchBlingStatus();
            window.location.href = `${api.defaults.baseURL || ''}/bling/auth`;
        } catch (error) {
            console.error('Erro ao reiniciar integracao do Bling:', error);
            setBlingStatus({ authenticated: false, error: 'Erro ao reiniciar integracao do Bling' });
        } finally {
            setRestartLoading(false);
        }
    };

    const handleSaveConfig = async () => {
        setConfigSaving(true);
        setConfigMessage(null);
        setConfigError(null);
        try {
            await api.post('/bling/config', {
                clientId: configForm.clientId.trim(),
                clientSecret: configForm.clientSecret.trim() || undefined,
                redirectUri: configForm.redirectUri.trim(),
                scopes: configForm.scopes.trim() || undefined,
            });
            setConfigMessage('Configuracao salva com sucesso');
            setConfigForm((prev) => ({ ...prev, clientSecret: '' }));
            await fetchBlingConfig();
        } catch (error) {
            console.error('Erro ao salvar configuracao do Bling:', error);
            setConfigError('Erro ao salvar configuracao do Bling');
        } finally {
            setConfigSaving(false);
        }
    };

    return (
        <Layout title="Integrações">
            <div className={styles.pageContainer}>
                <div className={styles.pageHeader}>
                    <div className={styles.headerContent}>
                        <h1 className={styles.pageTitle}>
                            <Link2 size={32} />
                            Integrações do Sistema
                        </h1>
                        <p className={styles.pageDescription}>
                            Gerencie as integrações do sistema SAGEx com outras plataformas e serviços.
                        </p>
                    </div>
                </div>

                <div className={styles.integrationsGrid}>
                    {/* Card de Integração Bling */}
                    <div className={`${styles.integrationCard} ${blingStatus?.authenticated ? styles.cardActive : ''}`}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitleGroup}>
                                <div className={styles.integrationLogo}>
                                    <span className={styles.logoText}>Bling</span>
                                </div>
                                <div>
                                    <h3 className={styles.cardTitle}>Emissor de Notas (Bling)</h3>
                                    <div className={styles.statusBadgeWrapper}>
                                        {loading ? (
                                            <span className={`${styles.statusBadge} ${styles.statusLoading}`}>Carregando...</span>
                                        ) : blingStatus?.authenticated ? (
                                            <span className={`${styles.statusBadge} ${styles.statusConnected}`}>
                                                <CheckCircle size={14} /> Conectado
                                            </span>
                                        ) : (
                                            <span className={`${styles.statusBadge} ${styles.statusDisconnected}`}>
                                                Não conectado
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.cardBody}>
                            {!blingStatus?.authenticated ? (
                                <>
                                    <p className={styles.cardDescription}>
                                        Para emitir notas fiscais, é necessário conectar com sua conta Bling:
                                    </p>
                                    <button
                                        onClick={handleConnectBling}
                                        className={styles.primaryButton}
                                        disabled={loading}
                                    >
                                        <ExternalLink size={18} />
                                        Conectar com Bling
                                    </button>
                                </>
                            ) : (
                                <div className={styles.connectedState}>
                                    <div className={styles.connectedInfo}>
                                        <p className={styles.infoRow}>
                                            <span className={styles.infoLabel}>Status:</span>
                                            <span className={styles.infoValueSuccess}>Autenticado e Pronto</span>
                                        </p>
                                        {blingStatus.expires_in && (
                                            <p className={styles.infoRow}>
                                                <span className={styles.infoLabel}>Sessão expira em:</span>
                                                <span className={styles.infoValue}>
                                                    {Math.floor(blingStatus.expires_in / 60)} minutos
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        onClick={fetchBlingStatus}
                                        className={styles.secondaryButton}
                                        title="Atualizar Status"
                                    >
                                        <RefreshCw size={16} /> Atualizar Status
                                    </button>
                                    <button
                                        onClick={handleRestartBling}
                                        className={styles.dangerButton}
                                        disabled={restartLoading}
                                        title="Reiniciar integracao para obter novo escopo"
                                    >
                                        {restartLoading ? 'Reiniciando...' : 'Reiniciar integracao'}
                                    </button>
                                </div>
                            )}

                            {blingStatus?.error && (
                                <div className={styles.errorMessage}>
                                    <Info size={16} />
                                    {blingStatus.error}
                                </div>
                            )}

                            <div className={styles.configSection}>
                                <h4 className={styles.configTitle}>Credenciais do Bling</h4>
                                <div className={styles.configRow}>
                                    <label className={styles.configLabel}>Client ID</label>
                                    <input
                                        className={styles.configInput}
                                        type="text"
                                        value={configForm.clientId}
                                        onChange={(e) => setConfigForm((prev) => ({ ...prev, clientId: e.target.value }))}
                                    />
                                </div>
                                <div className={styles.configRow}>
                                    <label className={styles.configLabel}>Client Secret</label>
                                    <input
                                        className={styles.configInput}
                                        type="password"
                                        value={configForm.clientSecret}
                                        placeholder={blingConfig?.hasClientSecret ? 'Deixe em branco para manter' : ''}
                                        onChange={(e) => setConfigForm((prev) => ({ ...prev, clientSecret: e.target.value }))}
                                    />
                                    {blingConfig?.hasClientSecret && (
                                        <span className={styles.configHint}>
                                            Segredo ja salvo. Preencha apenas se quiser substituir.
                                        </span>
                                    )}
                                </div>
                                <div className={styles.configRow}>
                                    <label className={styles.configLabel}>Redirect URI</label>
                                    <input
                                        className={styles.configInput}
                                        type="text"
                                        value={configForm.redirectUri}
                                        onChange={(e) => setConfigForm((prev) => ({ ...prev, redirectUri: e.target.value }))}
                                    />
                                </div>
                                <div className={styles.configRow}>
                                    <label className={styles.configLabel}>Scopes</label>
                                    <input
                                        className={styles.configInput}
                                        type="text"
                                        value={configForm.scopes}
                                        placeholder="ex: read,write"
                                        onChange={(e) => setConfigForm((prev) => ({ ...prev, scopes: e.target.value }))}
                                    />
                                    <span className={styles.configHint}>
                                        Para mudar escopo, salve e use "Reiniciar integracao".
                                    </span>
                                </div>
                                <button
                                    onClick={handleSaveConfig}
                                    className={styles.secondaryButton}
                                    disabled={configSaving}
                                >
                                    {configSaving ? 'Salvando...' : 'Salvar configuracao'}
                                </button>
                                {configMessage && (
                                    <div className={styles.successMessage}>
                                        <CheckCircle size={16} />
                                        {configMessage}
                                    </div>
                                )}
                                {configError && (
                                    <div className={styles.errorMessage}>
                                        <Info size={16} />
                                        {configError}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Futuras integrações podem ser adicionadas aqui */}
                </div>
            </div>
        </Layout>
    );
};

export default IntegracoesPage;
